/**
 * @description
 * AlertMessage component for the MediConnect Healthcare Information System
 * Provides a consistent way to display alert messages throughout the application.
 * 
 * Key features:
 * - Support for different alert types (error, warning, info, success)
 * - Customizable content and actions
 * - Accessible design with appropriate ARIA attributes
 * - HIPAA-compliant messaging
 * 
 * @dependencies
 * - react: For component rendering
 * - lucide-react: For icons
 */

import React from 'react';
import { 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  CheckCircle, 
  X 
} from 'lucide-react';

export type AlertType = 'error' | 'warning' | 'info' | 'success';

export interface AlertMessageProps {
  type: AlertType;
  title?: string;
  message: string;
  details?: string;
  onClose?: () => void;
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Gets the appropriate icon for the alert type
 */
const getAlertIcon = (type: AlertType) => {
  switch (type) {
    case 'error':
      return <AlertCircle className="w-5 h-5" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5" />;
    case 'info':
      return <Info className="w-5 h-5" />;
    case 'success':
      return <CheckCircle className="w-5 h-5" />;
  }
};

/**
 * Gets the appropriate title for the alert type if not provided
 */
const getDefaultTitle = (type: AlertType) => {
  switch (type) {
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    case 'success':
      return 'Success';
  }
};

/**
 * Gets the appropriate CSS classes for the alert type
 */
const getAlertClasses = (type: AlertType) => {
  switch (type) {
    case 'error':
      return {
        container: 'bg-red-50/80 border-red-200/60 shadow-red-500/5',
        icon: 'text-red-500',
        title: 'text-red-800',
        text: 'text-red-700/90'
      };
    case 'warning':
      return {
        container: 'bg-amber-50/80 border-amber-200/60 shadow-amber-500/5',
        icon: 'text-amber-500',
        title: 'text-amber-800',
        text: 'text-amber-700/90'
      };
    case 'info':
      return {
        container: 'bg-blue-50/80 border-blue-200/60 shadow-blue-500/5',
        icon: 'text-blue-500',
        title: 'text-blue-800',
        text: 'text-blue-700/90'
      };
    case 'success':
      return {
        container: 'bg-emerald-50/80 border-emerald-200/60 shadow-emerald-500/5',
        icon: 'text-emerald-500',
        title: 'text-emerald-800',
        text: 'text-emerald-700/90'
      };
  }
};

/**
 * AlertMessage component for displaying alert messages
 */
const AlertMessage: React.FC<AlertMessageProps> = ({
  type,
  title,
  message,
  details,
  onClose,
  actions,
  className = ''
}) => {
  const alertClasses = getAlertClasses(type);
  const alertIcon = getAlertIcon(type);
  const alertTitle = title || getDefaultTitle(type);
  
  return (
    <div 
      className={`
        p-5 rounded-2xl border backdrop-blur-md relative overflow-hidden
        ${alertClasses.container}
        ${className}
      `}
      role="alert"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      {/* Decorative side accent */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${alertClasses.icon.replace('text-', 'bg-')}`} />
      
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-full hover:bg-black/5"
          aria-label="Close alert"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      
      <div className="flex">
        <div className={`mr-4 mt-0.5 ${alertClasses.icon}`}>
          {alertIcon}
        </div>
        
        <div className="flex-1">
          <h4 className={`font-semibold mb-1 text-[15px] ${alertClasses.title}`}>
            {alertTitle}
          </h4>
          
          <div className={`text-sm leading-relaxed ${alertClasses.text}`}>
            <p>{message}</p>
            
            {details && (
              <p className="mt-2 text-[13px] opacity-90 break-words">
                {details}
              </p>
            )}
          </div>
          
          {actions && (
            <div className="mt-4 flex gap-3">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertMessage; 